import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, Send, Loader2, Check, Link2, Phone, Mail, MessageSquare } from 'lucide-react';
import api from '../../services/api';
import ContactPicker from '../common/ContactPicker';

// Staff-initiated "Propose Appointment": pick a recipient (a contact, or an ad-hoc
// external like a GAL / opposing counsel via raw email), choose to send open
// availability or propose specific times, and fire the invite. The recipient
// responds via a public tokenized link — no login required.
export default function ProposeAppointmentModal({ isOpen, onClose, caseId = null, onCreated }) {
  const [recipient, setRecipient] = useState(null); // {contact_id, name, email}
  const [channel, setChannel] = useState('email'); // 'email' | 'call' | 'sms'
  const [caseClient, setCaseClient] = useState(null); // {name, phone, has_phone, hot} for the call channel
  const [mode, setMode] = useState('open'); // 'open' | 'specific'
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState({}); // key -> {start,end}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // A1: the SUBJECT attorney. Slots and the booking come from this user, never the actor.
  const [attorneys, setAttorneys] = useState([]);
  const [attorneyId, setAttorneyId] = useState(null); // null = me
  // A3: matter is named or EXPLICITLY declined - never silently null.
  const [noMatter, setNoMatter] = useState(false);

  const loadSlots = useCallback(() => {
    setSlotsLoading(true);
    api.get('/appointments/my-slots', { params: { days: 14, ...(attorneyId ? { attorney_id: attorneyId } : {}) } })
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [attorneyId]);

  useEffect(() => {
    if (!isOpen) return;
    api.get('/appointments/bookable-attorneys')
      .then((r) => setAttorneys((r.data?.attorneys || []).filter((a) => a.authorised)))
      .catch(() => setAttorneys([]));
  }, [isOpen]);
  // subject changed -> the old subject's slots are meaningless
  useEffect(() => { setSlots([]); setPicked({}); }, [attorneyId]);

  useEffect(() => { if (isOpen && mode === 'specific' && slots.length === 0) loadSlots(); }, [isOpen, mode, slots.length, loadSlots]);
  useEffect(() => { if (!isOpen) { setRecipient(null); setMode('open'); setPicked({}); setResult(null); setError(''); setChannel('email'); setCaseClient(null); setAttorneyId(null); setNoMatter(false); } }, [isOpen]);
  useEffect(() => {
    if (isOpen && caseId) {
      api.get(`/cases/${caseId}/propose-call-eligibility`).then((r) => {
        const c = r.data || {};
        setCaseClient({ name: c.contact_name || 'the client', phone: c.phone || null,
                        has_phone: !!c.can_call, hot: !!c.hot, reason: c.reason,
                        can_sms: !!c.can_sms, sms_suppressed: !!c.sms_suppressed,
                        sms_reason: c.sms_reason });
      }).catch(() => setCaseClient(null));
    }
  }, [isOpen, caseId]);

  if (!isOpen) return null;

  const toggleSlot = (s) => {
    const key = s.start_time;
    setPicked((p) => { const n = { ...p }; if (n[key]) delete n[key]; else n[key] = { start: s.start_time, end: s.end_time }; return n; });
  };

  const submitCall = async () => {
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post(`/cases/${caseId}/propose-call`);
      setResult({ _call: true, ...data });
      if (onCreated) onCreated(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not queue the call.');
    } finally { setSubmitting(false); }
  };

  const submit = async () => {
    if (channel === 'call') return submitCall();
    if (channel === 'email' && !recipient?.email) { setError('Choose a recipient first.'); return; }
    if (channel === 'sms' && !caseClient?.can_sms) { setError(caseClient?.sms_reason || 'SMS is unavailable for this client.'); return; }
    const proposed = mode === 'specific' ? Object.values(picked) : null;
    if (mode === 'specific' && proposed.length === 0) { setError('Pick at least one time, or switch to open availability.'); return; }
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post('/appointments/requests', {
        channel,
        contact_id: channel === 'sms' ? null : (recipient?.contact_id || null),
        case_id: caseId || null,
        recipient_name: channel === 'sms' ? null : (recipient?.name || null),
        recipient_email: channel === 'sms' ? null : recipient?.email,
        proposed_slots: proposed,
        attorney_id: attorneyId || null,
        no_case: !caseId && noMatter,
      });
      setResult(data);
      if (onCreated) onCreated(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send the request.');
    } finally { setSubmitting(false); }
  };

  // group slots by date for the specific-times picker
  const byDate = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-virgo-navy-800 border border-virgo-teal-700/50 rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-virgo-teal-700/40">
          <div className="flex items-center gap-2 text-white font-semibold"><Calendar size={18} /> Propose Appointment</div>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={18} /></button>
        </div>
        {/* A1: WHO is being booked. The actor picks the subject; slots follow the subject. */}
        <div className="px-5 pt-3 space-y-2">
          {attorneys.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-virgo-teal-400 text-xs w-24 shrink-0">Appointment with</span>
              <select
                value={attorneyId || ''}
                onChange={(e) => setAttorneyId(e.target.value ? Number(e.target.value) : null)}
                className="flex-1 bg-virgo-navy-600 border border-virgo-teal-700/40 rounded-lg px-2 py-1.5 text-sm text-white"
              >
                <option value="">Myself</option>
                {attorneys.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
            </div>
          )}
          {!caseId && (
            <label className="flex items-center gap-2 text-xs text-virgo-teal-400">
              <input type="checkbox" checked={noMatter} onChange={(e) => setNoMatter(e.target.checked)} />
              This request is deliberately not tied to a matter
            </label>
          )}
        </div>

        {result ? (
          <div className="p-6 space-y-3 text-sm text-virgo-teal-100">
            {result._call ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300"><Check size={18} /> Astraea will call {result.contact_name || 'the client'} at {result.dialed_number}.</div>
                <div className="text-virgo-teal-400">Queued as a schedule-appointment call{result.hot ? ' (hot — dials first, same-day offerable)' : ''}. It dials within the calling window; the booking lands as a pending approval for you to confirm.</div>
                <button onClick={onClose} className="mt-2 px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white">Done</button>
              </>
            ) : (<>
            <div className="flex items-center gap-2 text-emerald-300"><Check size={18} /> Request sent to {result.channel === 'sms' ? (result.recipient_phone || 'the client') : result.recipient_email}.</div>
            <div className="text-virgo-teal-400">{result.channel === 'sms'
              ? (result.sms_sent ? 'The booking link was texted.' : `The text was not sent (${result.sms_reason || 'blocked'}).`)
              : (result.email_sent ? 'The invite email was delivered.' : 'The request was created (invite email pending).')}</div>
            <div className="flex items-center gap-2 text-xs text-virgo-teal-400 bg-virgo-navy-900 rounded-lg p-2 break-all"><Link2 size={13} className="shrink-0" />{result.respond_url}</div>
            <button onClick={onClose} className="mt-2 px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white">Done</button>
            </>)}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs text-virgo-teal-400 mb-1">Channel</label>
              <div className="flex gap-2">
                <button onClick={() => setChannel('email')}
                  className={`flex-1 text-sm px-3 py-2 rounded-lg border flex items-center justify-center gap-1.5 ${channel === 'email' ? 'bg-virgo-teal-600 text-white border-virgo-teal-500' : 'bg-virgo-navy-900 text-virgo-teal-300 border-virgo-teal-700/50 hover:bg-virgo-navy-700'}`}>
                  <Mail size={14} /> Email link
                </button>
                <button onClick={() => caseId && (caseClient?.has_phone) && setChannel('call')}
                  disabled={!caseId || !caseClient?.has_phone}
                  title={!caseId ? 'Open from a case to call the client' : (!caseClient?.has_phone ? 'Client contact has no phone number on record' : '')}
                  className={`flex-1 text-sm px-3 py-2 rounded-lg border flex items-center justify-center gap-1.5 ${channel === 'call' ? 'bg-virgo-teal-600 text-white border-virgo-teal-500' : 'bg-virgo-navy-900 text-virgo-teal-300 border-virgo-teal-700/50 hover:bg-virgo-navy-700'} disabled:opacity-40 disabled:cursor-not-allowed`}>
                  <Phone size={14} /> Astraea calls
                </button>
                <button onClick={() => caseId && caseClient?.can_sms && setChannel('sms')}
                  disabled={!caseId || !caseClient?.can_sms}
                  title={!caseId ? 'Open from a case to text the client' : (caseClient?.sms_reason || '')}
                  className={`flex-1 text-sm px-3 py-2 rounded-lg border flex items-center justify-center gap-1.5 ${channel === 'sms' ? 'bg-virgo-teal-600 text-white border-virgo-teal-500' : 'bg-virgo-navy-900 text-virgo-teal-300 border-virgo-teal-700/50 hover:bg-virgo-navy-700'} disabled:opacity-40 disabled:cursor-not-allowed`}>
                  <MessageSquare size={14} /> SMS
                </button>
              </div>
              {channel === 'call' && caseClient && (
                <p className="text-[11px] text-virgo-teal-400 mt-1">
                  Astraea will call <strong>{caseClient.name}</strong>{caseClient.phone ? ` at ${caseClient.phone}` : ''}
                  {caseClient.hot ? ' · hot case (dials first, same-day offerable)' : ''}.
                </p>
              )}
              {channel === 'call' && caseId && caseClient && !caseClient.has_phone && (
                <p className="text-[11px] text-amber-300 mt-1">This client has no phone on record — add one to enable the call.</p>
              )}
              {channel === 'sms' && caseClient && (
                <p className="text-[11px] text-virgo-teal-400 mt-1">
                  Texting <strong>{caseClient.name}</strong>{caseClient.phone ? ` at ${caseClient.phone}` : ''} — the booking link goes by text.
                </p>
              )}
              {caseId && caseClient && caseClient.sms_suppressed && (
                <p className="text-[11px] text-amber-300 mt-1">{caseClient.sms_reason}</p>
              )}
            </div>

            {channel === 'email' && (
            <div>
              <label className="block text-xs text-virgo-teal-400 mb-1">Recipient</label>
              {recipient ? (
                <div className="flex items-center justify-between bg-virgo-navy-900 rounded-lg px-3 py-2 text-sm text-white">
                  <span>{recipient.name || recipient.email}<span className="text-virgo-teal-500 ml-2 text-xs">{recipient.email}</span></span>
                  <button onClick={() => setRecipient(null)} className="text-virgo-teal-400 hover:text-white"><X size={14} /></button>
                </div>
              ) : (
                <ContactPicker allowRawEmail autoFocus onSelect={(c) => setRecipient(c)} placeholder="Search a contact, or type an email for an external…" />
              )}
            </div>
            )}

            {channel !== 'call' && (<>
            <div>
              <label className="block text-xs text-virgo-teal-400 mb-1">What to send</label>
              <div className="flex gap-2">
                {[['open', 'Send my open availability'], ['specific', 'Propose specific times']].map(([m, lbl]) => (
                  <button key={m} onClick={() => setMode(m)} className={`flex-1 text-sm px-3 py-2 rounded-lg border ${mode === m ? 'bg-virgo-teal-600 text-white border-virgo-teal-500' : 'bg-virgo-navy-900 text-virgo-teal-300 border-virgo-teal-700/50 hover:bg-virgo-navy-700'}`}>{lbl}</button>
                ))}
              </div>
            </div>

            {mode === 'specific' && (
              <div className="max-h-56 overflow-y-auto space-y-2 bg-virgo-navy-900 rounded-lg p-3">
                {slotsLoading && <div className="text-virgo-teal-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading your availability…</div>}
                {!slotsLoading && slots.length === 0 && <div className="text-virgo-teal-500 text-sm">No open slots in the next 14 days.</div>}
                {Object.entries(byDate).map(([d, list]) => (
                  <div key={d}>
                    <div className="text-[11px] text-virgo-teal-500 mb-1">{new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map((s) => (
                        <button key={s.start_time} onClick={() => toggleSlot(s)} className={`text-xs px-2 py-1 rounded ${picked[s.start_time] ? 'bg-virgo-teal-600 text-white' : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'}`}>{s.local_time}</button>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(picked).length > 0 && <div className="text-[11px] text-virgo-teal-400 pt-1">{Object.keys(picked).length} time(s) selected</div>}
              </div>
            )}

            </>
            )}
            <div className="text-[11px] text-virgo-teal-500">Phone appointment. {channel === 'call' ? 'Astraea offers times by voice; the booking lands as a pending approval.' : 'The recipient confirms via a personal link — no login needed.'}</div>
            {error && <div className="text-sm text-red-300">{error}</div>}
            <button onClick={submit} disabled={submitting || (channel === 'call' && !caseClient?.has_phone) || (channel === 'sms' && !caseClient?.can_sms)} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white disabled:opacity-50">
              {submitting ? <Loader2 size={15} className="animate-spin" /> : (channel === 'call' ? <Phone size={15} /> : (channel === 'sms' ? <MessageSquare size={15} /> : <Send size={15} />))} {channel === 'call' ? 'Queue Astraea call' : (channel === 'sms' ? 'Send text' : 'Send request')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
